import { Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { ABO_COLORS } from "../lib/campaignAboStyles";
import {
  SCHEDULED_SUMMARY_STATUS,
  formatCampaignBudgetAmount,
} from "../lib/helpers";

const STATUS_COPY = {
  [SCHEDULED_SUMMARY_STATUS.FULLY]: {
    label: "Fully scheduled",
    bgcolor: "#DCFCE7",
    color: "#15803D",
    percentColor: "#16A34A",
  },
  [SCHEDULED_SUMMARY_STATUS.PARTLY]: {
    label: "Partly scheduled",
    bgcolor: "#FFEDD5",
    color: "#C2410C",
    percentColor: "#EA580C",
  },
  [SCHEDULED_SUMMARY_STATUS.OVER]: {
    label: "Over budget",
    bgcolor: "#FEE2E2",
    color: "#B91C1C",
    percentColor: "#DC2626",
  },
};

export default function CampaignAboScheduledSummary({ rows = [], currencySymbol }) {
  if (!rows.length) return null;

  return (
    <Box
      sx={{
        mt: 2.5,
        border: `1px solid ${ABO_COLORS.border}`,
        borderRadius: 3,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: 2, pb: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: ABO_COLORS.label,
            lineHeight: 1.3,
          }}
        >
          SCHEDULED SUMMARY
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontSize: "0.8125rem",
            fontWeight: 500,
            color: ABO_COLORS.muted,
            lineHeight: 1.45,
          }}
        >
          Sum of Effective Budget across all slots against the daily budget frozen at save.
        </Typography>
      </Box>

      <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
        {rows.map((row) => {
          const status = STATUS_COPY[row.status] ?? STATUS_COPY[SCHEDULED_SUMMARY_STATUS.PARTLY];
          const dailyLabel = formatCampaignBudgetAmount(row.daily, currencySymbol);
          const scheduledLabel = formatCampaignBudgetAmount(row.scheduled, currencySymbol);
          return (
            <Stack
              key={row.mongoId}
              direction={{ xs: "column", md: "row" }}
              spacing={{ xs: 1, md: 4 }}
              alignItems={{ xs: "flex-start", md: "center" }}
              sx={{
                px: { xs: 2, md: 2.5 },
                py: 1.35,
                borderTop: `1px solid ${ABO_COLORS.border}`,
              }}
            >
              <Chip
                size="small"
                label={status.label}
                sx={{
                  height: 24,
                  flexShrink: 0,
                  px: 0.5,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  bgcolor: status.bgcolor,
                  color: status.color,
                  "& .MuiChip-label": { px: 1 },
                }}
              />
              <Tooltip title={row.name} arrow placement="top">
                <Typography
                  sx={{
                    flex: { md: "1 1 240px" },
                    minWidth: 0,
                    fontSize: "0.875rem",
                    fontWeight: 700,
                    color: ABO_COLORS.title,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.name}
                </Typography>
              </Tooltip>
              <Typography
                sx={{
                  flexShrink: 0,
                  minWidth: { md: 120 },
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: ABO_COLORS.body,
                }}
              >
                Daily {dailyLabel}
              </Typography>
              <Typography
                sx={{
                  flexShrink: 0,
                  minWidth: { md: 180 },
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  color: ABO_COLORS.body,
                }}
              >
                Scheduled{" "}
                {row.daily > 0 ? (
                  <>
                    <Box
                      component="span"
                      sx={{ fontWeight: 700, color: status.percentColor }}
                    >
                      {row.percent}%
                    </Box>{" "}
                    ({scheduledLabel})
                  </>
                ) : (
                  scheduledLabel
                )}
              </Typography>
            </Stack>
          );
        })}
      </Box>
    </Box>
  );
}
